(() => {
  const elementName = 'cws-home-search-results';

  if (!window.customElements || window.customElements.get(elementName)) return;

  const DEBOUNCE = 300;
  const MIN_LENGTH = 2;
  const SUGGEST_FIELDS =
    'author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor';

  class CwsHomeSearchResults extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.form = this.closest('.cws-home-header__main')?.querySelector('[data-cws-search]');
      this.input = this.form?.querySelector('input[name="q"]');

      if (!this.form || !this.input) return;

      this.isInitialized = true;
      this.cache = new Map();
      this.debounceTimer = null;
      this.requestToken = 0;

      this.handleInput = this.handleInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleFocus = this.handleFocus.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleSubmit = this.close.bind(this);
      this.handleFallbackSubmit = this.handleFallbackSubmit.bind(this);

      this.input.setAttribute('autocomplete', 'off');
      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('focus', this.handleFocus);
      this.form.addEventListener('keydown', this.handleKeydown);
      this.form.addEventListener('submit', this.handleSubmit);
      document.addEventListener('click', this.handleDocumentClick);
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      window.clearTimeout(this.debounceTimer);
      this.input.removeEventListener('input', this.handleInput);
      this.input.removeEventListener('focus', this.handleFocus);
      this.form.removeEventListener('keydown', this.handleKeydown);
      this.form.removeEventListener('submit', this.handleSubmit);
      document.removeEventListener('click', this.handleDocumentClick);
      this.isInitialized = false;
    }

    get limit() {
      const desktop = Number.parseInt(this.dataset.desktopLimit, 10) || 6;
      const mobile = Number.parseInt(this.dataset.mobileLimit, 10) || 3;

      return window.innerWidth > 767 ? desktop : mobile;
    }

    handleFocus() {
      if (this.innerHTML.trim() && this.input.value.trim().length >= MIN_LENGTH) this.open();
    }

    handleInput() {
      const query = this.input.value.trim();

      window.clearTimeout(this.debounceTimer);

      if (query.length < MIN_LENGTH) {
        this.close();
        this.innerHTML = '';
        return;
      }

      this.debounceTimer = window.setTimeout(() => this.search(query), DEBOUNCE);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.input.focus();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (this.hidden) return;

      const links = [...this.querySelectorAll('a[href]')].filter((link) => link.offsetParent !== null);
      if (!links.length) return;

      event.preventDefault();
      const current = links.indexOf(document.activeElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = current === -1 ? (step === 1 ? 0 : links.length - 1) : current + step;

      if (next < 0) this.input.focus();
      else if (next >= links.length) links[links.length - 1].focus();
      else links[next].focus();
    }

    handleDocumentClick(event) {
      if (this.hidden) return;
      if (this.contains(event.target) || this.form.contains(event.target)) return;

      this.close();
    }

    open() {
      this.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    }

    async search(query) {
      const key = `${query.toLowerCase()}_${this.limit}`;

      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }

      const token = (this.requestToken += 1);

      this.innerHTML = `<div class="cws-home-header__search-status">${this.dataset.loadingLabel || ''}</div>`;
      this.open();

      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        q: query,
        'resources[type]': this.dataset.resourceTypes || 'query,product',
        'resources[options][fields]': SUGGEST_FIELDS,
        'resources[limit]': String(this.limit),
        section_id: 'predictive-search'
      });

      try {
        const response = await fetch(`${root}search/suggest?${params}`);
        if (!response.ok) throw new Error(String(response.status));

        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const html = parsed.querySelector('#shopify-section-predictive-search')?.innerHTML ?? '';

        // A newer keystroke already fired; its result wins.
        if (token !== this.requestToken) return;

        this.cache.set(key, html);
        this.render(html);
      } catch {
        if (token !== this.requestToken) return;

        // Leave the customer with the normal form rather than a stuck panel.
        this.innerHTML = '';
        this.close();
      }
    }

    render(html) {
      this.innerHTML = html;
      this.adoptSubmitControls();

      if (this.textContent.trim()) this.open();
      else this.close();
    }

    // The native predictive markup ends in a bare <button>, the "view all" control.
    // It defaults to type="submit" and works natively because the results sit inside
    // the search form. This panel is a sibling of the form, so the button has no form
    // to submit. Associate it explicitly.
    adoptSubmitControls() {
      const buttons = [...this.querySelectorAll('button')].filter(
        (button) => button.getAttribute('type') !== 'button'
      );

      buttons.forEach((button) => {
        if (this.form.id) button.setAttribute('form', this.form.id);
        else button.addEventListener('click', this.handleFallbackSubmit);
      });
    }

    handleFallbackSubmit(event) {
      event.preventDefault();
      this.form.requestSubmit?.() ?? this.form.submit();
    }
  }

  window.customElements.define(elementName, CwsHomeSearchResults);
})();
